// client/src/scheduling/utils/mlBudget.js
// Model-suggested budgets in Data Integrity -> IDD / Budgets. Pure functions only: the
// tab (IDDReview.jsx) renders what these decide.
//
// The interface is return_job_ml/docs/CONTRACT-ML-SUGGESTED-BUDGETS.md (v1.2, 2026-10-19:
// the tighter Agrees, half hours, ml-declined; owned by return_job_ml; ask that session or
// Hutson before changing a rule here). The state rules compare UNROUNDED numbers:
//   section 4  one state per job, first match wins:
//              no-budget -> agrees (inside 50% AND within max(2h, 10%) of the model) ->
//              close (inside 80%) -> stale (= last year's final budget, outside 80%) ->
//              set-this-year (outside 80%)
//   section 5  the split of the total across tasks (largest remainder, fallbacks)
//   section 6  the audit Source + Notes
// The prediction is a TOTAL of in-scope hours (it excludes the Other / OOS row).
//
// Half hours (Hutson, 2026-10-19): every model number is shown, split and applied to the
// nearest 0.5h, so a budget is never set to 24.27h. The exact PredHours is kept only in
// the audit Notes (pred=), for measuring the model after the season.

export const ML_STATE = {
  NO_BUDGET: 'no-budget',
  AGREES: 'agrees',
  CLOSE: 'close',
  STALE: 'stale',
  SET_THIS_YEAR: 'set-this-year',
};

// Contract section 4 table, as the tab labels them.
//   outside       the "Outside model range" filter (the outliers a manager should work first)
//   applyPrimary  Apply model is the first button (no budget, close, stale)
//   keep          the manager's budget gets a "Keep Xh" button (+ optional why?); keeping
//                 it is audited ml-declined (every state the model would change)
export const ML_STATE_META = {
  'no-budget': { label: 'No budget', outside: false, applyPrimary: true, keep: false },
  agrees: { label: 'Agrees with model', outside: false, applyPrimary: false, keep: false },
  close: { label: 'Close to model', outside: false, applyPrimary: true, keep: true },
  stale: { label: 'Stale roll-forward', outside: true, applyPrimary: true, keep: true },
  'set-this-year': { label: 'Differs from model', outside: true, applyPrimary: false, keep: true },
};

// "Agrees" is tight (Hutson, 2026-10-19): inside the 50% range AND within max(2h, 10% of
// PredHours) of the model's number. The 50% range alone let a rolled-forward 28h agree
// with a 22.93h prediction (range 17.93-29.85).
export const AGREE_MIN_HOURS = 2;
export const AGREE_SHARE = 0.10;

export const AUTOFILL_MAX_HOURS = 10;   // mirrors the PY auto-fill rule
const EPS = 0.05;                       // "exactly equal" for decimal budgets, as the tab uses

const OOS_SUBJECT = 'Other';
const isOOS = (t) => t.TaskSubject === OOS_SUBJECT || Number(t.TaskOrdinal) === 999;
const num = (v) => (v == null || v === '' ? 0 : Number(v) || 0);

export const roundHalf = (v) => Math.round(num(v) * 2) / 2;

// The model's total as the tab shows and applies it.
export function modelHours(pred) {
  return roundHalf(pred?.PredHours);
}

export function hasFlag(pred, flag) {
  return String(pred?.Flags || '').split('|').map(s => s.trim()).includes(flag);
}

// Purpose starting DEV = stub data (a placeholder run, never a model number).
export function isStubPrediction(pred) {
  return /^DEV/i.test(String(pred?.Purpose || '')) || hasFlag(pred, 'STUB');
}

// "Last year's final budget" (v1.1): the sum of the PY job's task budgets, falling back to
// rprs.JobsHistory.BudgetHours (job.PYBudget). null when neither exists.
export function pyFinalBudget(taskRows, job) {
  const pyRows = (taskRows || []).filter(t => t.PYBudget != null);
  if (pyRows.length > 0) {
    const sum = pyRows.reduce((s, t) => s + num(t.PYBudget), 0);
    if (sum > 0) return round2(sum);
  }
  return job && job.PYBudget != null && Number(job.PYBudget) > 0 ? round2(Number(job.PYBudget)) : null;
}

// "Last year: actual" (v1.1): in-scope WIP, i.e. the task sum WITHOUT the Other / OOS row,
// like the model's AnchorHours. Falls back to AnchorHours when the PY job has no task rows
// in swa-rprs yet (they exist only for closed PY jobs). -> { hours, fromModel }
export function pyInScopeActual(taskRows, pred) {
  const pyRows = (taskRows || []).filter(t => t.PYWIP != null && !isOOS(t));
  if (pyRows.length > 0) return { hours: round2(pyRows.reduce((s, t) => s + num(t.PYWIP), 0)), fromModel: false };
  if (pred && pred.AnchorHours != null) return { hours: round2(Number(pred.AnchorHours)), fromModel: true };
  return { hours: null, fromModel: false };
}

const inRange = (b, lo, hi) => lo != null && hi != null && b >= Number(lo) - EPS && b <= Number(hi) + EPS;

export function isRolledForward(budget, pyFinal) {
  return pyFinal != null && num(budget) > 0 && Math.abs(num(budget) - pyFinal) < EPS;
}

export function outside80(budget, pred) {
  return !inRange(num(budget), pred?.Lo80, pred?.Hi80);
}

export function agreeTolerance(pred) {
  return Math.max(AGREE_MIN_HOURS, AGREE_SHARE * num(pred?.PredHours));
}

export function agreesWithModel(budget, pred) {
  const b = num(budget);
  return b > 0 && inRange(b, pred?.Lo50, pred?.Hi50)
    && Math.abs(b - num(pred?.PredHours)) <= agreeTolerance(pred) + EPS;
}

// One state per job; first match wins (contract section 4, Agrees tightened 2026-10-19).
export function classifyBudget({ budget, pyFinal, pred }) {
  const b = num(budget);
  if (b <= 0) return ML_STATE.NO_BUDGET;
  if (agreesWithModel(b, pred)) return ML_STATE.AGREES;
  if (inRange(b, pred.Lo80, pred.Hi80)) return ML_STATE.CLOSE;
  if (isRolledForward(b, pyFinal)) return ML_STATE.STALE;
  return ML_STATE.SET_THIS_YEAR;
}

// The context line's "This year" qualifier.
export function thisYearLabel(budget, pyFinal) {
  if (num(budget) <= 0) return 'none';
  return isRolledForward(budget, pyFinal) ? 'rolled forward' : 'set this year';
}

// Effective budget of a row: unsaved edit > saved draft > synced, per task, when the task
// data means anything (else the job-level values queryIDDJobs reconciled). Lifted verbatim
// from IDDTable so the parent can classify rows for the filter chips.
export function effectiveBudgetOf(job, taskRows, taskEdits) {
  const rows = taskRows || [];
  const edits = taskEdits || {};
  const wip = job.CYActualHours || 0;
  const hasMeaningfulTasks = rows.some(t =>
    (t.BudgetHours || 0) > 0 || (t.WIPHours || 0) > 0 || t.SavedRemainingHrs != null || edits[t.TaskSubject] != null);
  if (hasMeaningfulTasks) {
    let sumRem = 0, sumWip = 0;
    for (const t of rows) {
      const edited = edits[t.TaskSubject];
      const saved = t.SavedRemainingHrs;
      const orig = (t.BudgetHours || 0) - (t.WIPHours || 0);
      sumRem += edited != null ? Number(edited) : saved != null ? Number(saved) : orig;
      sumWip += t.WIPHours || 0;
    }
    return { effectiveBudget: sumRem + sumWip, effectiveRemaining: sumRem, hasMeaningfulTasks };
  }
  if (job.NewRemainingHrs != null) {
    const rem = Number(job.NewRemainingHrs);
    return { effectiveBudget: Number(job.NewBudgetHours != null ? job.NewBudgetHours : rem + wip), effectiveRemaining: rem, hasMeaningfulTasks };
  }
  return { effectiveBudget: job.BudgetHours, effectiveRemaining: job.RemainingHours, hasMeaningfulTasks };
}

// Contract section 5: split PredHours across THIS year's editable tasks.
//   weights = PY WIP per task, over tasks that exist this year (no Other / OOS, no PY-only);
//   fallback 1: PY task budget shares; fallback 2: everything on the Preparation* task.
//   budget_t by largest remainder over HALF-HOUR units, so every task budget is a multiple
//   of 0.5h and the tasks sum to PredHours rounded to the nearest 0.5h;
//   remaining_t = max(0, budget_t - this year's WIP_t).
// -> { remaining: {subject: h}, budgets: {subject: h}, total, basis, note } | null when there
//    is no task to put hours on. Every eligible task is in the maps (0 included), so applying
//    replaces a rolled-forward split rather than leaving last year's numbers on some tasks.
export function splitPrediction(predHours, taskRows) {
  const units = Math.round(num(predHours) * 2);   // half hours
  const tasks = (taskRows || []).filter(t => !isOOS(t) && t.InCY !== false && t.InCY !== 0);
  if (tasks.length === 0 || units <= 0) return null;

  let basis = 'py-wip';
  let weights = tasks.map(t => Math.max(0, num(t.PYWIP)));
  if (weights.reduce((a, b) => a + b, 0) <= 0) {
    basis = 'py-budget';
    weights = tasks.map(t => Math.max(0, num(t.PYBudget)));
  }
  if (weights.reduce((a, b) => a + b, 0) <= 0) {
    const prep = tasks.findIndex(t => /^preparation/i.test(String(t.TaskSubject || '').trim()));
    if (prep < 0) return null;
    basis = 'prep-only';
    weights = tasks.map((_, i) => (i === prep ? 1 : 0));
  }

  const budgetsArr = largestRemainder(units, weights).map(u => u / 2);
  const budgets = {}, remaining = {};
  tasks.forEach((t, i) => {
    budgets[t.TaskSubject] = budgetsArr[i];
    remaining[t.TaskSubject] = Math.max(0, round2(budgetsArr[i] - num(t.WIPHours)));
  });
  const total = tasks.reduce((s, t) => s + remaining[t.TaskSubject] + num(t.WIPHours), 0);
  const note = basis === 'prep-only' ? 'task mix: no prior year; adjust'
    : basis === 'py-budget' ? "task mix from last year's budget" : "task mix from last year's hours";
  return { remaining, budgets, total: round2(total), basis, note };
}

// Section 199A is no longer budgeted (Kate Fluker #731, Hutson 2026-11-03): PE still carries
// a dead 199A task budget forward (389 open jobs, 1,164.5h, ~3h of 199A time ever logged), so
// an undecided job's rows are shown and saved with it folded into Preparation:
//   - this year's 199A remaining moves onto Preparation (199A keeps only its own WIP), and a
//     saved 199A remaining likewise;
//   - last year's 199A hours and budget fold into Preparation's, so every split (model, last
//     year) weights Preparation instead of 199A.
// Totals are unchanged. Preparation = the task named exactly that, else the first
// "Preparation*" one; no Preparation task (none today) -> rows unchanged.
// -> a new array; the Preparation row carries Moved199A (hours moved) when anything moved.
export const is199A = (t) => /199A/i.test(String(t.TaskSubject || ''));

export function fold199AIntoPrep(rows) {
  const list = rows || [];
  const fold = list.filter(t => !isOOS(t) && is199A(t));
  if (fold.length === 0) return list;
  const inCY = (t) => t.InCY !== false && t.InCY !== 0;
  const prepOf = (re) => list.find(t => !isOOS(t) && inCY(t) && re.test(String(t.TaskSubject || '').trim()));
  const prep = prepOf(/^preparation$/i) || prepOf(/^preparation/i);
  if (!prep) return list;

  const addNullable = (a, b) => (a == null && b == null ? a : round2(num(a) + num(b)));
  const out = { ...prep };
  let moved = 0;
  const folded = new Map();
  for (const t of fold) {
    const f = { ...t };
    if (inCY(t)) {
      const rem = Math.max(0, num(t.BudgetHours) - num(t.WIPHours));
      if (rem > 0) { out.BudgetHours = round2(num(out.BudgetHours) + rem); f.BudgetHours = round2(num(t.BudgetHours) - rem); moved += rem; }
      if (num(t.SavedRemainingHrs) > 0) {
        const prepRem = out.SavedRemainingHrs != null ? num(out.SavedRemainingHrs) : num(prep.BudgetHours) - num(prep.WIPHours);
        out.SavedRemainingHrs = round2(prepRem + num(t.SavedRemainingHrs));
        f.SavedRemainingHrs = 0;
      }
    }
    out.PYWIP = addNullable(out.PYWIP, t.PYWIP);
    out.PYBudget = addNullable(out.PYBudget, t.PYBudget);
    if (t.PYWIP != null) f.PYWIP = 0;
    if (t.PYBudget != null) f.PYBudget = 0;
    folded.set(t, f);
  }
  if (moved > 0) out.Moved199A = round2(moved);
  return list.map(t => (t === prep ? out : folded.get(t) || t));
}

// Integer parts summing exactly to `total`, proportional to `weights`; ties to the larger
// weight, then to the earlier task (stable, deterministic).
export function largestRemainder(total, weights) {
  const W = weights.reduce((a, b) => a + b, 0);
  if (W <= 0) return weights.map(() => 0);
  const raw = weights.map(w => (total * w) / W);
  const out = raw.map(Math.floor);
  let left = total - out.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r), w: weights[i] }))
    .sort((a, b) => (b.frac - a.frac) || (b.w - a.w) || (a.i - b.i));
  for (let k = 0; left > 0 && k < order.length; k++, left--) out[order[k].i] += 1;
  return out;
}

// Contract section 6 Notes: 'run=<RunId> pred=<PredHours> [ai=<tag>] [reason=<...>]'.
export function mlNotes(pred, { ai, reason } = {}) {
  if (!pred) return undefined;
  const parts = [`run=${pred.RunId}`, `pred=${round2(Number(pred.PredHours))}`];
  if (ai) parts.push(`ai=${ai}`);
  if (reason) parts.push(`reason=${String(reason).replace(/\s+/g, '_')}`);
  return parts.join(' ');
}

const sameTaskMap = (a, b) => {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const k of keys) {
    const x = a?.[k], y = b?.[k];
    if (x == null && y == null) continue;
    if (x == null || y == null || Math.abs(Number(x) - Number(y)) > 0.001) return false;
  }
  return true;
};

// The audit Source for a Save of a job with a prediction (section 6):
//   applied the model, saved as is   -> ml-suggestion (or ml-autofill for the <=10h pre-fill)
//   applied, then edited the tasks   -> ml-tweak
//   never applied, budget unchanged, and it doesn't agree with the model (Close, Stale or
//   Differs: the model was offered and turned down) -> ml-declined (was ml-kept in v1.1,
//   renamed 2026-10-19: "kept" read as keeping the model's number)
//   otherwise (typed their own number, or an agreeing budget) -> null (= the existing
//   ai-* / py-suggestion / manual rules decide)
export function mlSaveSource({ applied, editedTasks, budgetChanged, declined }) {
  if (applied) {
    if (sameTaskMap(applied.tasks, editedTasks || {})) return applied.kind === 'autofill' ? 'ml-autofill' : 'ml-suggestion';
    return 'ml-tweak';
  }
  if (!budgetChanged && declined) return 'ml-declined';
  return null;
}

// True when keeping this budget turns the model down (it is set and doesn't agree).
export function declinesModel(budget, pred) {
  return num(budget) > 0 && !agreesWithModel(budget, pred);
}

// A model-side number on screen: nearest half hour ("22.5", "23"). The manager's own
// budget is shown as entered (the tab's fmtHours), so "Keep 24.27h" keeps what it says.
export function fmtH(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  const n = roundHalf(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function round2(n) { return Math.round(n * 100) / 100; }
